import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { RotateCcw, X } from "lucide-react";

// Small dismissible banner surfacing the most recent import with one-tap undo.
export default function LastImportBanner({ target, tabId, onChanged, refreshToken }) {
  const [batch, setBatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const params = tabId ? { tab_id: tabId } : { kind: target === "tab" ? "" : "deals" };

  const load = useCallback(async () => {
    try {
      const res = await api.get("/import/history", { params }).then((r) => r.data);
      setBatch(Array.isArray(res) && res.length ? res[0] : null);
      setDismissed(false);
    } catch (_) { setBatch(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabId, target]);

  // Reloads on mount AND whenever refreshToken changes (e.g. after a commit).
  useEffect(() => { load(); }, [load, refreshToken]);

  if (!batch || dismissed) return null;

  const undo = async () => {
    setBusy(true);
    try {
      await api.post(`/import/undo/${batch.id}`);
      setBatch(null);
      await onChanged?.();
      await load();
    } finally { setBusy(false); }
  };

  return (
    <div
      className="hud-frame flex items-center justify-between gap-3 px-4 py-2.5 border-l-2 border-[hsl(var(--oxblood))]"
      data-testid="last-import-banner"
    >
      <div className="mono text-[11px] tracking-widest uppercase text-muted-foreground">
        Last import ·{" "}
        <span className="text-[hsl(var(--oxblood))] font-semibold">
          {batch.count} {batch.count === 1 ? "row" : "rows"}
        </span>{" "}
        · {new Date(batch.created_at).toLocaleString()}
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={undo}
          disabled={busy}
          className="hud-btn !px-2 !py-1 flex items-center gap-1 !text-[hsl(var(--oxblood))]"
          data-testid="last-import-undo"
        >
          <RotateCcw size={11} /> {busy ? "UNDOING…" : "UNDO"}
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="hud-btn !px-2 !py-1"
          data-testid="last-import-dismiss"
          title="Dismiss"
        >
          <X size={11} />
        </button>
      </div>
    </div>
  );
}
